/**
 * Authentication Service
 *
 * Provides methods for authentication, session management, and JWT handling.
 * This is the central service for all authentication-related functionality.
 */

import { Session, User, AuthError } from '@supabase/supabase-js';
import { JwtClaims } from '../types';
import { QueryClient } from '@tanstack/react-query';
import { invalidateUserQueries } from '@/services/query/user-queries';
import * as SupabaseAuth from '@/services/supabase/auth';

// Cache keys for local storage
const SESSION_CACHE_KEY = 'auth_session';
const USER_CACHE_KEY = 'auth_user';

/**
 * Parse JWT token to extract claims
 *
 * @param token - JWT token string
 * @returns Parsed JWT claims or null if invalid
 */
export function parseJwt(token: string): JwtClaims | null {
  try {
    // Split the token and get the payload part (second segment)
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    // Replace characters for base64 decoding
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    // Decode the base64 string
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    // Parse the JSON payload
    return JSON.parse(jsonPayload) as JwtClaims;
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
}

/**
 * Get the current session
 *
 * @returns Promise resolving to the current session
 */
export async function getSession(): Promise<Session | null> {
  const { session, error } = await SupabaseAuth.getSession();

  if (error) {
    console.error('Error getting session:', error);
    return null;
  }

  return session;
}

/**
 * Get the current user
 *
 * @returns Promise resolving to the current user
 */
export async function getUser(): Promise<User | null> {
  const { user, error } = await SupabaseAuth.getUser();

  if (error) {
    console.error('Error getting user:', error);
    return null;
  }

  return user;
}

/**
 * Extract tenant ID from session
 *
 * @param session - Current session
 * @returns Tenant ID or null if not found
 */
export function extractTenantId(session: Session | null): string | null {
  if (!session) return null;

  const token = session.access_token;
  const claims = parseJwt(token);

  return claims?.tenant_id || null;
}

/**
 * Sign in with email and password
 *
 * @param email - User email
 * @param password - User password
 * @returns Promise resolving to success or error
 */
export async function signIn(email: string, password: string): Promise<{ error: Error | null }> {
  const { error } = await SupabaseAuth.signInWithPassword(email, password);
  return { error: error as Error | null };
}

/**
 * Sign up with email and password
 *
 * @param email - User email
 * @param password - User password
 * @returns Promise resolving to success or error
 */
export async function signUp(email: string, password: string): Promise<{ error: Error | null }> {
  const { error } = await SupabaseAuth.signUp(email, password);
  return { error: error as Error | null };
}

/**
 * Sign out the current user
 * Clears all authentication data and redirects to auth page
 */
export async function signOut(): Promise<void> {
  try {
    // Create a query client for cache invalidation
    const queryClient = new QueryClient();

    // Invalidate all user-related queries
    invalidateUserQueries(queryClient);

    // Sign out from Supabase
    const { error } = await SupabaseAuth.signOut();
    if (error) {
      console.error('Error signing out:', error);
    }

    // Clear local storage auth data
    localStorage.removeItem(SESSION_CACHE_KEY);
    localStorage.removeItem(USER_CACHE_KEY);

    // Clear any other auth-related items
    const authKeys = Object.keys(localStorage).filter(key =>
      key.includes('supabase') ||
      key.includes('sb-') ||
      key.includes('auth')
    );

    authKeys.forEach(key => localStorage.removeItem(key));

    // Redirect to auth page
    window.location.href = '/auth';
  } catch (error) {
    console.error('Error during sign out:', error);
    // Despite error, still try to redirect
    window.location.href = '/auth';
  }
}

/**
 * Reset password for an email
 *
 * @param email - User email
 * @returns Promise resolving to success or error
 */
export async function resetPassword(email: string): Promise<{ error: Error | null }> {
  const { error } = await SupabaseAuth.resetPassword(email);
  return { error: error as Error | null };
}

/**
 * Refresh the current session
 *
 * @returns Promise resolving to the refreshed session
 */
export async function refreshSession(): Promise<Session | null> {
  const { session, error } = await SupabaseAuth.refreshSession();

  if (error) {
    console.error('Error refreshing session:', error);
    return null;
  }

  return session;
}

/**
 * Set up an auth state change listener
 *
 * @param callback - Function to call when auth state changes
 * @returns Subscription object with unsubscribe method
 */
export function onAuthStateChange(
  callback: (event: string, session: Session | null) => void
) {
  return SupabaseAuth.onAuthStateChange(callback);
}
